import { parseSanitizedHtml } from "@/utils/sanitizeHtml";
import { useEffect } from "react";
import { FaRegCircle, FaCircle } from "react-icons/fa6";
import Helpers from "@/Helpers";

type ContentOption = {
  value?: number | string;
  label?: string;
};

type ContentItem = {
  id: number;
  page?: number | string;
  text?: string;
  description?: string;
  name?: string;
};

type SectionContent = {
  elements?: {
    items?: ContentItem[];
    options?: ContentOption[];
    headers?: string[];
  };
  htmlDecoded?: {
    instructions?: string;
    reminder?: string;
  };
};

type CurrentSection = {
  name?: string;
  page?: number;
  formId: number;
  content?: Record<string, unknown>;
};

type SectionData = {
  currentSection: CurrentSection;
  page?: number;
  nextCount?: number;
  lang?: string;
  isAdmin?: boolean;
  showValidations?: boolean;
  updateIssueCount?: (count: number) => void;
  registerSection: (order: number, total: number) => void;
};

type AnxietiesProps = {
  sectionData: SectionData;
  values: Record<string, string>;
  setValue: (itemId: string, selectedValue: string) => void;
};

export default function Anxieties(props: AnxietiesProps) {
  const { sectionData, values, setValue } = props;
  const currentSection = sectionData?.currentSection;

  const content: SectionContent = Helpers.getSectionContent(currentSection, sectionData?.lang);
  const elements = Helpers.getSectionElements(content);

  const contentItems = Array.isArray(elements?.items) ? elements.items : [];
  const contentOptions = Array.isArray(elements?.options) ? elements.options : [];
  const instructionsHtml = Helpers.getSectionHtmlValue(content, "instructions");
  const reminderHtml = Helpers.getSectionHtmlValue(content, "reminder");

  function getIssueCountForPage(validationValues: Record<string, string>, page: number) {
    if (!Array.isArray(contentItems)) return 0;

    const currentPageItems = contentItems.filter((item) => Number(item?.page) === Number(page));
    return currentPageItems.filter((item) => Number(validationValues?.[currentSection.formId + item.id] || 0) === 0).length;
  }

  useEffect(() => {
    sectionData.registerSection(600, contentItems.length);
  }, [currentSection?.name]);

  useEffect(() => {
    const nextCount = Number(sectionData?.nextCount || 0);
    const activePage = Number(sectionData?.page || 1);
    const issueCount = nextCount > 0 ? getIssueCountForPage(values, activePage) : 0;
    sectionData?.updateIssueCount?.(issueCount);
  }, [contentItems, currentSection?.formId, sectionData?.nextCount, sectionData?.page, sectionData?.updateIssueCount, values]);

  const pageItems = contentItems.filter((item) => Number(item?.page) === Number(sectionData?.page || 1));

  useEffect(() => {
    if (!sectionData?.isAdmin) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const activeTag = document.activeElement?.tagName?.toLowerCase();
      if (activeTag === "input" || activeTag === "textarea" || activeTag === "select") return;

      const key = (e.key || "").toLowerCase();
      if (!/^[0-9]$/.test(key)) return;

      const optionCount = contentOptions.length;
      if (optionCount === 0) return;

      const selectedValue = key === "0" ? 10 : Number(key);
      if (selectedValue < 1 || selectedValue > optionCount) return;

      const nextUnanswered = pageItems.find((item) => {
        const value = Number(values?.[currentSection.formId + item.id] || 0);
        return value === 0;
      });

      if (!nextUnanswered) return;

      e.preventDefault();
      setValue(String(nextUnanswered.id), String(selectedValue));
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [sectionData?.isAdmin, contentOptions.length, pageItems, values, currentSection?.formId, setValue]);

  if (!sectionData) return null;

  function getItemValue(itemId: number) {
    return Number(values?.[currentSection.formId + itemId] || 0);
  }

  function onSelectValue(itemId: number, selectedValue: number) {
    setValue(String(itemId), String(selectedValue));
  }

  return (
    <div className="anxieties page tableMode">
      <div className="instructions left">{parseSanitizedHtml((Number(sectionData?.page || 1) === 1 ? instructionsHtml : reminderHtml) || "")}</div>

      <table className="tableData anxietiesTable center highlightEven no-select">
        <thead>
          <tr>
            <th className="question columnheader columndata">{elements?.headers?.[0] || "Question"}</th>
            {contentOptions.map((option, index) => {
              const optionValue = Number(option?.value || index + 1);
              const optionLabel = option?.label || "";
              return (
                <th key={`h-${optionValue}`} className="optionHeader columnheader columndata">
                  {optionLabel}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {pageItems.map((item) => {
            const itemValue = getItemValue(item.id);
            const itemText = item.text || item.description || item.name || "";

            return (
              <tr key={item.id} className={`anxietiesItem${sectionData?.showValidations && !itemValue ? " invalid" : ""}`}>
                <td className="questionCell">
                  <div className="questionText">
                    {item.id}. {itemText}
                  </div>
                </td>
                {contentOptions.map((option, index) => {
                  const optionValue = Number(option?.value || index + 1);
                  const optionLabel = option?.label || "";
                  const checked = itemValue === optionValue;

                  return (
                    <td
                      key={`${item.id}-${optionValue}`}
                      className={`answerCell${checked ? " checked" : ""}`}
                      onClick={() => onSelectValue(item.id, optionValue)}
                      title={optionLabel}
                    >
                      <div className="optionIcon" aria-hidden="true">
                        {checked ? <FaCircle className="actionIcon" /> : <FaRegCircle className="actionIcon" />}
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

